import { Badge, Flex, Heading, Spinner, Text } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { CustomIcon } from 'components/common/custom-icon';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTabs } from 'components/common/tabs';
import { ImagesTab } from 'components/machine/dialogs/health-check/images.tab';
import { RawDataTab } from 'components/machine/dialogs/health-check/raw-data.tab';
import { HealthCheckResultsTab } from 'components/machine/dialogs/health-check/results.tab';
import { MachineContext } from 'contexts/machine.context';
import { lightFormat } from 'date-fns';
import { CustomIconPath } from 'enums/custom.enums';
import { t } from 'i18next';
import { IButton } from 'interfaces/i-buttons';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { HealthCheckType, QueueState } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import useMachineStatusStore from 'stores/use-machine-status-store';

const COMPONENT_NAME = 'HealthCheckDialog';

interface IProps {
  onClose: () => void;
}

enum TabKey {
  Results = 'Results',
  BallInserter = 'BallInserter',
  RawData = 'RawData',
}

export const HealthCheckResults = ({
  activeTab,
  setActiveTab,
  showAllResults,
}: {
  activeTab: TabKey;
  setActiveTab: (tab: TabKey) => void;
  showAllResults: boolean;
}) => {
  return (
    <Flex direction="column">
      <CommonTabs
        tabs={[
          {
            label: t('common.results'),
            value: TabKey.Results,
            content: <HealthCheckResultsTab showAllResults={showAllResults} />,
          },
          {
            label: t('common.images'),
            value: TabKey.BallInserter,
            content: <ImagesTab />,
          },
          {
            label: t('common.raw-data'),
            value: TabKey.RawData,
            content: <RawDataTab />,
          },
        ]}
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as TabKey)}
        gap="0"
      />
    </Flex>
  );
};

// turns the health check object into a downloadable JSON file
const downloadJSON = (data: object, filename: string) => {
  const jsonStr = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const HealthCheckDialog = ({ onClose }: IProps) => {
  const queueState = useMachineStatusStore((s) => s.queueState);

  const {
    healthChecking,
    machine,
    healthCheckResults,
    startHealthCheck,
    reconnect,
  } = useContext(MachineContext);

  const [activeTab, setActiveTab] = useState<TabKey>(TabKey.Results);
  const [showAllResults, setShowAllResults] = useState(false);

  const buttons: IButton[] = useMemo(() => {
    if (healthCheckResults) {
      return [
        {
          label: t('common.verbose').toString(),
          icon: showAllResults ? (
            <CustomIcon icon={CustomIconPath.SwitchOn} />
          ) : (
            <CustomIcon icon={CustomIconPath.SwitchOff} />
          ),
          onClick: () => setShowAllResults((prev) => !prev),
          invisible: activeTab !== TabKey.Results,
        },
        {
          // re-running health check again will clear healthCheckResults
          label: t('common.re-run-health-check').toString(),
          variant: 'solid',
          onClick: () => startHealthCheck(HealthCheckType.FULL),
        },
        {
          label: t('common.save-results').toString(),
          onClick: () => {
            const filename = `${machine.machineID} Health Check (${lightFormat(
              new Date(),
              LOCAL_DATE_FORMAT
            )}).json`;
            downloadJSON(healthCheckResults, filename);
          },
          color: RADIX.COLOR.SUCCESS,
        },
      ];
    }

    const getRunHealthCheckButton = (
      healthChecking: boolean,
      queueState: QueueState
    ): IButton => {
      const disconnected = [QueueState.UserDC, QueueState.MachineDC].includes(
        queueState
      );
      const onClick = disconnected
        ? () => reconnect()
        : () => startHealthCheck(HealthCheckType.FULL);

      let label = t('common.run-health-check').toString();
      let color = RADIX.COLOR.ACCENT;

      if (healthChecking) {
        label = t('common.running-health-check').toString();
      }

      if (queueState === QueueState.UserDC) {
        label = t('common.click-to-reconnect').toString();
        color = RADIX.COLOR.WARNING;
      }

      if (queueState === QueueState.MachineDC) {
        label = t('common.machine-offline').toString();
        color = RADIX.COLOR.DANGER;
      }

      const showSpinner = healthChecking && !disconnected;

      return {
        label,
        disabled: healthChecking && !disconnected,
        color,
        variant: 'solid',
        suffixIcon: showSpinner ? <Spinner /> : undefined,
        onClick,
      };
    };

    return [getRunHealthCheckButton(healthChecking, queueState)];
  }, [
    healthChecking,
    healthCheckResults,
    activeTab,
    showAllResults,
    queueState,
  ]);

  const title = machine
    ? MachineContextHelper.getMachineMenuLabel(machine)
    : undefined;

  const width = healthCheckResults
    ? RADIX.DIALOG.WIDTH.LG
    : RADIX.DIALOG.WIDTH.MD;

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        title={
          <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
            <Heading size={RADIX.HEADING.SIZE.MODAL_TITLE} mb="0" truncate>
              {t('common.run-health-check')}
            </Heading>
            {title && <Badge>{title}</Badge>}
          </Flex>
        }
        width={width}
        content={
          healthCheckResults ? (
            <HealthCheckResults
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              showAllResults={showAllResults}
            />
          ) : (
            <Flex direction="column">
              <Text size="2">
                <ReactMarkdown
                  children={t('common.health-check-description')}
                />
              </Text>
            </Flex>
          )
        }
        buttons={buttons}
        onClose={onClose}
        modal
      />
    </ErrorBoundary>
  );
};
